import React, { useCallback } from 'react';
import { Button } from '@tanium/react-button';
import styled from '@tanium/react-emotion';
import { Icon } from '@tanium/react-graphics';
import { IconButton } from '@tanium/react-icon-button';
import { SimpleTooltip } from '@tanium/react-tooltip';
import { css } from '@tanium/style-system';

/**
 * An object describing an action to be displayed in a Ribbon.
 */
export interface RibbonAction {
  /**
   * The icon to represent the action. If no icon is specified, the action is shown with the given
   * label as a text button.
   */
  icon?: Icon;
  /**
   * Whether the icon should always be visible, or only visible when the mouse
   * hovers over the ribbon (default). A typical use case where this would be
   * `true` would be when the ribbon represents something that has been
   * favorited (and the action exists to "unfavorite" that item), so the
   * "favorite" star would be persistently visible.
   */
  alwaysVisible?: boolean;
  /**
   * The label to use for the action. This label is used if no icon is specified. It may also be
   * used in a tooltip and for accessibility purposes.
   */
  label: string;
  /**
   * Callback to invoke when the action is invoked.
   */
  onAction: () => void;
}

interface Props extends RibbonAction {
  /**
   * Boolean to indicate whether the action should be visible or not.
   * If `alwaysVisible` is `true`, this prop is ignored.
   * Defaults to `false`.
   */
  visible?: boolean;
}

type ActionWrapperProps = Pick<Props, 'alwaysVisible' | 'visible'>;

const ActionWrapper = styled('span')<ActionWrapperProps>(
  css({
    ml: 1,
  }),
  ({ alwaysVisible, visible }) => ({
    display: alwaysVisible || visible ? 'inline-block' : 'none',
  }),
);

export const Action = ({ alwaysVisible, icon, label, onAction, visible }: Props) => {
  const handleClick = useCallback(() => {
    onAction();
  }, [onAction]);

  return (
    <ActionWrapper alwaysVisible={alwaysVisible} visible={visible}>
      {icon ? (
        <SimpleTooltip content={label}>
          {({ ref }) => (
            <IconButton
              ref={ref}
              height={16}
              width={16}
              icon={icon}
              label={label}
              onClick={handleClick}
            />
          )}
        </SimpleTooltip>
      ) : (
        <Button onClick={handleClick} type="button">
          {label}
        </Button>
      )}
    </ActionWrapper>
  );
};
